import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { CurrentUser, AuthTokens } from '@cc-consulting-nv/ccsdk';
import { getSdk, storeTokens, clearAuth, getStoredTokens, storeUser, getStoredUser } from '@/lib/sdk';

interface AuthContextValue {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithMagicLink: (email: string, code: string) => Promise<void>;
  requestMagicLink: (email: string) => Promise<void>;
  requestAuthCode: (email: string) => Promise<void>;
  loginWithOAuth: (
    provider: string,
    code: string,
    redirectUri?: string,
    extraData?: { id_token?: string | null; user?: string | null },
  ) => Promise<void>;
  register: (payload: {
    email: string;
    password: string;
    username: string;
    displayName?: string;
  }) => Promise<void>;
  setTokensAndFetchUser: (tokens: AuthTokens) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(() => getStoredUser<CurrentUser>());
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = user !== null;

  // Fetch current user profile from SDK and persist
  const fetchAndSetUser = useCallback(async () => {
    const sdk = getSdk();
    const profile = await sdk.getCurrentUser();
    if (profile) {
      setUser(profile);
      storeUser(profile);
    }
    return profile;
  }, []);

  // Handle tokens after any auth flow — store, sync SDK, fetch user
  const handleAuthSuccess = useCallback(
    async (tokens: AuthTokens) => {
      storeTokens(tokens);
      await fetchAndSetUser();
    },
    [fetchAndSetUser],
  );

  // Restore session on mount
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const tokens = getStoredTokens();

      if (!tokens?.accessToken) {
        setIsLoading(false);
        return;
      }

      try {
        await fetchAndSetUser();
      } catch (err) {
        clearAuth();
        setUser(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [fetchAndSetUser]);

  const login = useCallback(
    async (email: string, password: string) => {
      const sdk = getSdk();
      const tokens = await sdk.login(email, password);
      await handleAuthSuccess(tokens);
    },
    [handleAuthSuccess],
  );

  const loginWithMagicLink = useCallback(
    async (email: string, code: string) => {
      const sdk = getSdk();
      const tokens = await sdk.loginWithMagicLink(email, code);
      await handleAuthSuccess(tokens);
    },
    [handleAuthSuccess],
  );

  const requestMagicLink = useCallback(async (email: string) => {
    const sdk = getSdk();
    await sdk.requestMagicLink(email);
  }, []);

  const requestAuthCode = useCallback(async (email: string) => {
    const sdk = getSdk();
    await sdk.requestAuthCode(email);
  }, []);

  const loginWithOAuth = useCallback(
    async (
      provider: string,
      code: string,
      redirectUri?: string,
      extraData?: { id_token?: string | null; user?: string | null },
    ) => {
      const sdk = getSdk();
      const tokens = await sdk.loginWithOAuth(provider, code, redirectUri, extraData);
      await handleAuthSuccess(tokens);
    },
    [handleAuthSuccess],
  );

  const register = useCallback(
    async (payload: {
      email: string;
      password: string;
      username: string;
      displayName?: string;
    }) => {
      const sdk = getSdk();
      const tokens = await sdk.register(payload);
      await handleAuthSuccess(tokens);
    },
    [handleAuthSuccess],
  );

  const setTokensAndFetchUser = useCallback(
    async (tokens: AuthTokens) => {
      await handleAuthSuccess(tokens);
    },
    [handleAuthSuccess],
  );

  const logout = useCallback(async () => {
    try {
      const sdk = getSdk();
      await sdk.logout();
    } catch {
      // Ignore errors during logout
    }
    clearAuth();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      loginWithMagicLink,
      requestMagicLink,
      requestAuthCode,
      loginWithOAuth,
      register,
      setTokensAndFetchUser,
      logout,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      login,
      loginWithMagicLink,
      requestMagicLink,
      requestAuthCode,
      loginWithOAuth,
      register,
      setTokensAndFetchUser,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
